import React from 'react';
import { HeaderVehicleSearch } from '@/components/index';
import {
  SearchEmailForm,
  SearchPhoneForm,
  SearchUsernameForm,
} from '@/reports/index';
import { Box, styled } from '@/theme/index';
import { DynamicSearchForm } from './DynamicSearchForm/DynamicSearchForm';
import { InternalSearchTab, SearchFormProps } from './interfaces';

const SearchForms = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  maxHeight: '600px',
  padding: theme.spacing(2.5, 0),
  boxShadow: '0px 1px 2px rgba(117, 117, 117, 0.5)',

  '&.footer': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },

  '@keyframes inAnimation': {
    '0%': {
      maxHeight: 0,
      padding: 0,
    },
    '100%': {
      maxHeight: '600px',
      padding: theme.spacing(2.5, 0),
    },
  },

  '@keyframes outAnimation': {
    '0%': {
      maxHeight: '600px',
      padding: theme.spacing(2.5, 0),
    },
    '100%': {
      maxHeight: '0',
      padding: 0,
    },
  },
}));

interface SearchFormsContainerProps {
  displayName: string;
  internalTabs?: InternalSearchTab[];
  placement: 'footer' | 'header' | 'dashboard';
  onSearchSuccess: SearchFormProps['onSuccess'];
  onSearchError: SearchFormProps['onError'];
  closeForm: () => void;
  combinedPropertySearch?: boolean;
  isSearchFormLocked?: boolean;
  shouldHideSearchForm?: boolean;
  isInternationalVinTest?: boolean;
}

export function SearchFormsContainer({
  displayName,
  internalTabs = undefined,
  placement = 'header',
  onSearchSuccess,
  onSearchError,
  closeForm,
  combinedPropertySearch,
  isSearchFormLocked,
  shouldHideSearchForm,
  isInternationalVinTest,
}: SearchFormsContainerProps) {
  const filteredInternalTabs = combinedPropertySearch ? [] : internalTabs;
  const isEmailTab = /email/i.test(displayName);
  const isPhoneTab = /phone/i.test(displayName);
  const isPropertyTab = /property/i.test(displayName);
  const isPersonTab = /people/i.test(displayName);
  const isUsernameTab = /username/i.test(displayName);
  const isVehicleTab = /vehicle/i.test(displayName);

  const classes: { [key: string]: boolean } = {
    footer: placement === 'footer',
  };
  const activeClasses = Object.keys(classes)
    .filter((key) => classes[key])
    .join(' ');

  if (shouldHideSearchForm) {
    return null;
  }

  if (isInternationalVinTest) {
    return (
      <SearchForms className={activeClasses}>
        <Box maxWidth="1250px" mx="auto">
          <HeaderVehicleSearch
            onSuccess={onSearchSuccess}
            onError={onSearchError}
            closeForm={closeForm}
            claimReport={false}
            placement={placement}
          />
        </Box>
      </SearchForms>
    );
  }

  const currentForm = () => {
    switch (true) {
      case isPersonTab: {
        return (
          <DynamicSearchForm
            internalTabs={internalTabs || []}
            defaultForm="SearchPersonForm"
            closeForm={closeForm}
            placement={placement}
            onSearchSuccess={onSearchSuccess}
            onSearchError={onSearchError}
          />
        );
      }
      case isPhoneTab: {
        return (
          <SearchPhoneForm
            onSuccess={onSearchSuccess}
            onError={onSearchError}
            closeForm={closeForm}
            placement={placement}
          />
        );
      }
      case isPropertyTab: {
        return (
          <DynamicSearchForm
            internalTabs={filteredInternalTabs || []}
            defaultForm="SearchPropertyForm"
            closeForm={closeForm}
            placement={placement}
            onSearchSuccess={onSearchSuccess}
            onSearchError={onSearchError}
            combinedPropertySearch={combinedPropertySearch}
          />
        );
      }
      case isEmailTab: {
        return (
          <SearchEmailForm
            onSuccess={onSearchSuccess}
            onError={onSearchError}
            closeForm={closeForm}
            placement={placement}
          />
        );
      }
      case isUsernameTab: {
        return (
          <SearchUsernameForm
            onSuccess={onSearchSuccess}
            onError={onSearchError}
            closeForm={closeForm}
            placement={placement}
          />
        );
      }
      case isVehicleTab: {
        return (
          <HeaderVehicleSearch
            onSuccess={onSearchSuccess}
            onError={onSearchError}
            closeForm={closeForm}
            claimReport={false}
            placement={placement}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  if (isSearchFormLocked) {
    return null;
  }

  return (
    <SearchForms className={activeClasses}>
      <Box maxWidth="1250px" mx="auto">
        {/* TODO: Get the SearchForm to smoothly change its height whenever it changes */}
        {currentForm()}
      </Box>
    </SearchForms>
  );
}
